<template>
  <div class="flex flex-col gap-4">
    <!-- Above the statement's punched edge, so the date picker opens over it. -->
    <div class="relative z-20 flex flex-wrap items-center gap-3">
      <div class="flex gap-2">
        <button
          v-for="option in statusOptions"
          :key="option.value"
          type="button"
          class="rounded-md border px-3 py-1.5 text-sm transition"
          :class="
            statusFilter === option.value
              ? 'border-obsidian-900 bg-obsidian-900 text-white'
              : 'border-obsidian-200 bg-white text-obsidian-700 hover:border-obsidian-300'
          "
          @click="statusFilter = option.value"
        >
          {{ option.label }}
        </button>
      </div>
      <div class="w-72">
        <DateRangeFilterPicker
          v-model:start-date="startDate"
          v-model:end-date="endDate"
          :placeholder="t('dateRange')"
          variant="default"
        />
      </div>
      <Select
        v-if="(accountsQuery.data.value?.length ?? 0) > 1"
        v-model="accountIban"
        :options="ibanOptions"
        :placeholder="t('allAccounts')"
        class="w-72"
      />
      <CVButton
        v-if="canCreateTestEntry"
        class="ml-auto"
        variant="secondary"
        size="sm"
        @click="showTestEntryForm = true"
      >
        <FlaskConicalIcon class="size-4" />
        {{ t('createTestEntry') }}
      </CVButton>
    </div>

    <div v-if="isLoading" class="flex justify-center py-10">
      <Spinner class="text-hazel-600" />
    </div>

    <EntityListExceptions
      v-else
      :status="entriesQuery.status.value"
      :error="entriesQuery.error.value"
      :items-count="entries.length"
      :no-items-text="t('noEntries')"
      small
    />

    <div v-if="entries.length || leavingCount" class="mx-auto w-full max-w-[112rem]">
      <div class="relative">
        <div
          aria-hidden="true"
          class="pointer-events-none absolute inset-0 hidden lg:grid lg:grid-cols-[40rem_minmax(0,1fr)] 2xl:grid-cols-[minmax(40rem,1fr)_minmax(0,1fr)]"
        >
          <div class="rounded-t-md bg-[#fffdf8] shadow-md ring-1 ring-hazel-200" />
        </div>
        <div
          aria-hidden="true"
          class="pointer-events-none absolute inset-y-0 left-0 z-10 hidden w-6 bg-[radial-gradient(circle,theme(colors.hazel.200)_3.5px,transparent_4px)] bg-[length:100%_1.25rem] lg:block"
        />
        <div
          class="grid lg:grid-cols-[40rem_minmax(0,1fr)] 2xl:grid-cols-[minmax(40rem,1fr)_minmax(0,1fr)]"
        >
          <div
            class="relative rounded-t-md bg-[#fffdf8] pb-2 pl-8 pr-5 pt-4 font-mono text-walnut-700 after:absolute after:bottom-0 after:left-8 after:right-5 after:border-b-4 after:border-double after:border-walnut-400 lg:bg-transparent"
          >
            <div class="flex items-baseline justify-between gap-4">
              <span class="text-sm font-semibold uppercase tracking-[0.2em]">{{
                t('statementHeader')
              }}</span>
              <span v-if="headerIban" class="text-[11px] tabular-nums">{{ headerIban }}</span>
            </div>
            <div
              class="mt-3 grid grid-cols-[5.5rem_minmax(0,1fr)_auto] gap-x-4 text-[11px] uppercase tracking-wider text-walnut-500"
            >
              <span>{{ t('columnDate') }}</span>
              <span>{{ t('columnTransaction') }}</span>
              <span class="text-right">{{ t('columnAmount') }}</span>
            </div>
          </div>
          <div
            class="hidden items-end pb-2 pl-4 text-xs font-semibold uppercase tracking-wider text-obsidian-500 lg:flex"
          >
            {{ t('assignmentHeader') }}
          </div>
        </div>
        <TransitionGroup :css="false" @leave="handleRowLeave">
          <BankStatementRow
            v-for="entry in entries"
            :key="entry.id"
            :data-entry-id="entry.id"
            :entry="entry"
            :can-confirm="canConfirm"
            :is-confirming="confirmingId === entry.id"
            :show-account-iban="!headerIban"
            :show-legal-entity="isLegalEntityFilterVisible && !legalEntityId"
            @confirm="handleConfirm(entry.id, $event)"
            @choose-target="handleChooseTarget"
          />
        </TransitionGroup>
      </div>

      <!-- The statement continues: its paper ends in a torn edge that carries "load more". -->
      <div
        v-if="entriesQuery.hasNextPage.value"
        class="grid lg:grid-cols-[40rem_minmax(0,1fr)] 2xl:grid-cols-[minmax(40rem,1fr)_minmax(0,1fr)]"
      >
        <!-- The button straddles the tear, so it sits outside the clipped paper. -->
        <div class="relative pb-5">
          <div class="drop-shadow-[0_3px_2px_rgb(68_52_30/0.14)]">
            <div
              class="relative h-10 bg-[#fffdf8] lg:-mx-px lg:border-x lg:border-hazel-200"
              :style="{ clipPath: tornEdgeClipPath }"
            >
              <div
                aria-hidden="true"
                class="pointer-events-none absolute inset-y-0 left-0 hidden w-6 bg-[radial-gradient(circle,theme(colors.hazel.200)_3.5px,transparent_4px)] bg-[length:100%_1.25rem] lg:block"
              />
            </div>
          </div>
          <CVButton
            outline
            size="sm"
            class="absolute left-1/2 top-[calc(3rem)] -translate-x-1/2 -translate-y-1/2 shadow-sm"
            :is-loading="entriesQuery.isFetchingNextPage.value"
            @click="() => entriesQuery.fetchNextPage()"
          >
            {{ t('loadMore') }}
          </CVButton>
        </div>
      </div>
    </div>

    <Modal v-model="showTestEntryForm" show-close :close-on-click-outside="false">
      <BankStatementTestEntryForm v-if="showTestEntryForm" @complete="showTestEntryForm = false" />
    </Modal>

    <BankStatementTargetPickerModal
      v-model="showTargetPicker"
      v-model:search="targetSearch"
      :entry="activeEntry"
      :items="openAmountsQuery.data.value ?? []"
      :is-loading="openAmountsQuery.isFetching.value"
      :status="openAmountsQuery.status.value"
      :error="openAmountsQuery.error.value"
      :is-confirming="confirmingId === activeEntry?.id"
      @select="handleSelectTarget"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAbility } from '@casl/vue';
import { useRouteQueryRef } from '@/hooks/use-route-query-ref';
import CVButton from '@/components/buttons/CVButton.vue';
import Select from '@/components/Select.vue';
import Spinner from '@/components/icons/Spinner.vue';
import EntityListExceptions from '@/components/EntityListExceptions.vue';
import DateRangeFilterPicker from '@/components/DateRangeFilterPicker.vue';
import { useLegalEntityFilter } from '@/hooks/use-legal-entity-filter';
import { DateTime } from 'luxon';
import BankStatementRow from './BankStatementRow.vue';
import BankStatementTargetPickerModal from './BankStatementTargetPickerModal.vue';
import BankStatementTestEntryForm from './BankStatementTestEntryForm.vue';
import Modal from '@/components/Modal.vue';
import { FlaskConicalIcon } from 'lucide-vue-next';
import {
  useBankStatementAccounts,
  useBankStatementEntries,
  useBankStatementOpenAmounts,
  useConfirmBankStatementAction,
} from '@/queries/use-bank-statements';
import { toPaymentTarget } from '@/api/bank-statements.service';
import {
  BankStatementAction,
  BankStatementListStatus,
  BankStatementOpenAmountType,
  type BankStatementEntryDto,
  type BankStatementOpenAmountDto,
  type ConfirmBankStatementActionDto,
} from '@carvia/ros-client-types';
import { refDebounced, usePreferredReducedMotion } from '@vueuse/core';
import confetti from 'canvas-confetti';

const { t } = useI18n();
const ability = useAbility();
const canConfirm = computed(() => ability.can('create', 'Payment'));
// Hand-made entries for testing matching, posted to the bank sync's ingest. Hidden in production;
// the ingest itself is open to anyone allowed to create Bank statement entries.
const canCreateTestEntry = computed(
  () =>
    import.meta.env.VITE_ENVIRONMENT !== 'production' &&
    ability.can('create', 'BankStatementEntry'),
);
const showTestEntryForm = ref(false);

const statusFilter = useRouteQueryRef<BankStatementListStatus>(
  'statementStatus',
  BankStatementListStatus.UNMATCHED,
  'string',
);
const accountIban = useRouteQueryRef('accountIban', '', 'string');

const accountsQuery = useBankStatementAccounts(true);
const startDate = useRouteQueryRef('bookedFrom', null as Date | null, 'date');
const endDate = useRouteQueryRef('bookedTo', null as Date | null, 'date');
const dateRange = computed(() => ({
  startDate: startDate.value
    ? (DateTime.fromJSDate(startDate.value).startOf('day').toISO() ?? undefined)
    : undefined,
  endDate: endDate.value
    ? (DateTime.fromJSDate(endDate.value).plus({ days: 1 }).startOf('day').toISO() ?? undefined)
    : undefined,
}));
const { legalEntityId, isLegalEntityFilterVisible } = useLegalEntityFilter();
const entriesQuery = useBankStatementEntries(
  statusFilter,
  accountIban,
  legalEntityId,
  dateRange,
  true,
);
const isLoading = computed(() => entriesQuery.isLoading.value);
const confirmMutation = useConfirmBankStatementAction();
const confirmingId = ref<string | null>(null);

const entries = computed(() => entriesQuery.data.value?.pages.flatMap((page) => page.items) ?? []);

const statusOptions = computed(() => [
  { value: BankStatementListStatus.UNMATCHED, label: t('unmatched') },
  { value: BankStatementListStatus.CONFIRMED, label: t('confirmed') },
  { value: BankStatementListStatus.IGNORED, label: t('ignored') },
]);

const ibanOptions = computed(() => [
  { label: t('allAccounts'), value: '' },
  ...(accountsQuery.data.value ?? []).map((iban) => ({ label: iban, value: iban })),
]);

const headerIban = computed(() => {
  const ibans = accountsQuery.data.value ?? [];
  return accountIban.value || (ibans.length === 1 ? ibans[0] : null);
});

const activeEntry = ref<BankStatementEntryDto | null>(null);
const showTargetPicker = ref(false);
const targetSearch = ref('');
const debouncedTargetSearch = refDebounced(targetSearch, 300);

const openAmountsQuery = useBankStatementOpenAmounts(
  computed(() => activeEntry.value?.id ?? null),
  debouncedTargetSearch,
  showTargetPicker,
);
const handleChooseTarget = (entry: BankStatementEntryDto) => {
  activeEntry.value = entry;
  targetSearch.value = '';
  showTargetPicker.value = true;
};

const tornEdgeClipPath = buildTornEdgeClipPath(48);

/** A ragged bottom edge with fixed pseudo-random teeth, so it looks torn but never jumps. */
function buildTornEdgeClipPath(teeth: number): string {
  const points = ['0 0', '100% 0'];
  for (let i = teeth; i >= 0; i--) {
    const jitter = Math.abs((Math.sin(i * 12.9898) * 43758.5453) % 1);
    const depth = i % 2 === 0 ? 2 + jitter * 3 : 8 + jitter * 6;
    points.push(`${((i / teeth) * 100).toFixed(2)}% calc(100% - ${depth.toFixed(1)}px)`);
  }
  return `polygon(${points.join(', ')})`;
}

// Only rows the user just handled get stamped and collapse; filter switches and refetches swap the
// list at once. Keyed by entry id, holding the action that was confirmed.
const confirmedActions = new Map<string, BankStatementAction | null>();
const reducedMotion = usePreferredReducedMotion();
const leavingCount = ref(0);

const handleConfirm = async (entryId: string, body: ConfirmBankStatementActionDto) => {
  confirmingId.value = entryId;
  confirmedActions.set(entryId, body.action ?? null);
  try {
    // The state this row showed, so a double submit is rejected instead of applied twice.
    const expectedAction = entries.value.find((entry) => entry.id === entryId)?.action ?? null;
    await confirmMutation.mutateAsync({ entryId, body: { ...body, expectedAction } });
    await nextTick();
  } finally {
    confirmedActions.delete(entryId);
    confirmingId.value = null;
  }
};

const stampFor = (action: BankStatementAction | null) =>
  action === BankStatementAction.IGNORE
    ? { label: t('stampIgnored'), variant: 'ignored' }
    : action == null
      ? { label: t('stampReopened'), variant: 'reopened' }
      : { label: t('stampBooked'), variant: 'booked' };

// A small jolt of the whole page when the stamp hits the paper.
const shakePage = () => {
  document
    .getElementById('app')
    ?.animate(
      [
        { transform: 'translate(0, 0)' },
        { transform: 'translate(-5px, 3px)' },
        { transform: 'translate(5px, -2px)' },
        { transform: 'translate(-3px, 2px)' },
        { transform: 'translate(2px, -1px)' },
        { transform: 'translate(0, 0)' },
      ],
      { duration: 220, easing: 'ease-out' },
    );
};

// Clouds of paper dust puffing out from under the stamp. Flat, big and weightless so the
// overlapping circles drift apart and fade instead of falling like confetti.
const dustColors = ['#a39c87', '#b2ac9b', '#d7bf90', '#dec9a2'];
// Own canvas so a CSS blur can soften the particle edges into clouds.
let dustCanvas: HTMLCanvasElement | null = null;
let dustConfetti: confetti.CreateTypes | null = null;
const getDustConfetti = () => {
  if (!dustConfetti) {
    dustCanvas = document.createElement('canvas');
    dustCanvas.className = 'bank-statement-dust';
    document.body.appendChild(dustCanvas);
    dustConfetti = confetti.create(dustCanvas, { resize: true, disableForReducedMotion: true });
  }
  return dustConfetti;
};
onBeforeUnmount(() => {
  dustConfetti?.reset();
  dustCanvas?.remove();
  dustConfetti = null;
  dustCanvas = null;
});

const puffDust = (stampEl: HTMLElement) => {
  const shoot = getDustConfetti();
  const rect = stampEl.getBoundingClientRect();
  const y = (rect.top + rect.height / 2) / window.innerHeight;
  const common = {
    colors: dustColors,
    shapes: ['circle' as const],
    flat: true,
    gravity: -0.03,
    drift: 0,
    decay: 0.9,
    ticks: 90,
  };
  const puff = (x: number, angle: number, spread: number) => {
    for (const scalar of [2.5, 4]) {
      shoot({
        ...common,
        origin: { x: x / window.innerWidth, y },
        particleCount: 6,
        angle,
        spread,
        scalar,
        startVelocity: 10,
      });
    }
  };
  puff(rect.left + rect.width / 2, 90, 360);
  puff(rect.left, 180, 70);
  puff(rect.right, 0, 70);
};

const handleRowLeave = async (el: Element, done: () => void) => {
  const row = el as HTMLElement;
  const entryId = row.dataset.entryId;
  if (!entryId || !confirmedActions.has(entryId) || reducedMotion.value === 'reduce') {
    done();
    return;
  }
  const stamp = stampFor(confirmedActions.get(entryId) ?? null);
  confirmedActions.delete(entryId);

  row.style.pointerEvents = 'none';
  row.style.overflow = 'clip';
  leavingCount.value++;
  try {
    // The statement cell is `relative`, so the stamp lands on the paper side of the row.
    const stampEl = document.createElement('span');
    stampEl.className = `bank-statement-stamp bank-statement-stamp--${stamp.variant}`;
    stampEl.textContent = stamp.label;
    (row.firstElementChild ?? row).appendChild(stampEl);
    await stampEl.animate(
      [
        { opacity: 0, transform: 'translate(-50%, -50%) rotate(-8deg) scale(1.8)' },
        { opacity: 1, transform: 'translate(-50%, -50%) rotate(-8deg) scale(0.94)', offset: 0.75 },
        { opacity: 1, transform: 'translate(-50%, -50%) rotate(-8deg) scale(1)' },
      ],
      { duration: 200, easing: 'cubic-bezier(0.5, 0, 0.75, 0)', fill: 'forwards' },
    ).finished;
    shakePage();
    puffDust(stampEl);
    await row.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: 220,
      delay: 380,
      easing: 'ease-in',
      fill: 'forwards',
    }).finished;
    await row.animate([{ height: `${row.offsetHeight}px` }, { height: '0px' }], {
      duration: 220,
      easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
      fill: 'forwards',
    }).finished;
  } catch {
    // Cancelled animations (e.g. the list unmounted) just finish the leave.
  } finally {
    leavingCount.value--;
    done();
  }
};

const handleSelectTarget = async (
  targets: BankStatementOpenAmountDto[],
  existingPaymentId: string | null,
) => {
  const entry = activeEntry.value;
  const [target] = targets;
  if (!entry || !target) return;
  await handleConfirm(
    entry.id,
    existingPaymentId
      ? { action: BankStatementAction.LINK_PAYMENT, paymentId: existingPaymentId }
      : target.type === BankStatementOpenAmountType.SETTLEMENT
        ? {
            action: BankStatementAction.LINK_SETTLEMENT,
            settlementDetailId: target.settlementDetail?.id,
          }
        : {
            action: BankStatementAction.CREATE_PAYMENT,
            targets: targets.map(toPaymentTarget),
          },
  );
  showTargetPicker.value = false;
};
</script>

<style>
/* Not scoped: the stamp is created in the leave hook, outside of the template. */
.bank-statement-stamp {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 20;
  padding: 0.25rem 1.25rem;
  border: 5px double currentColor;
  border-radius: 0.375rem;
  font-family: theme('fontFamily.mono');
  font-size: 1.75rem;
  line-height: 1.2;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  mix-blend-mode: multiply;
  background: rgb(255 253 248 / 0.6);
}

.bank-statement-dust {
  position: fixed;
  inset: 0;
  z-index: 60;
  width: 100%;
  height: 100%;
  pointer-events: none;
  filter: blur(5px);
  opacity: 0.45;
}

.bank-statement-stamp--booked {
  color: theme('colors.green.800');
}

.bank-statement-stamp--ignored {
  color: theme('colors.red.800');
}

.bank-statement-stamp--reopened {
  color: theme('colors.walnut.600');
}
</style>

<i18n lang="json">
{
  "en": {
    "createTestEntry": "Create test entry",
    "unmatched": "To match",
    "confirmed": "Confirmed",
    "ignored": "Ignored",
    "allAccounts": "All accounts",
    "dateRange": "Booking date",
    "noEntries": "No bank statement entries",
    "statementHeader": "Account statement",
    "columnDate": "Booked / Value",
    "columnTransaction": "Transaction",
    "columnAmount": "Amount EUR",
    "assignmentHeader": "Assignment",
    "loadMore": "Load more",
    "stampBooked": "Booked",
    "stampIgnored": "Ignored",
    "stampReopened": "Reopened"
  },
  "de": {
    "createTestEntry": "Testeintrag anlegen",
    "unmatched": "Offen",
    "confirmed": "Bestätigt",
    "ignored": "Ignoriert",
    "allAccounts": "Alle Konten",
    "dateRange": "Buchungsdatum",
    "noEntries": "Keine Kontoauszugszeilen",
    "statementHeader": "Kontoauszug",
    "columnDate": "Buchung / Valuta",
    "columnTransaction": "Vorgang",
    "columnAmount": "Betrag EUR",
    "assignmentHeader": "Zuordnung",
    "loadMore": "Mehr laden",
    "stampBooked": "Gebucht",
    "stampIgnored": "Ignoriert",
    "stampReopened": "Wieder offen"
  },
  "es": {
    "createTestEntry": "Crear entrada de prueba",
    "unmatched": "Por conciliar",
    "confirmed": "Confirmados",
    "ignored": "Ignorados",
    "allAccounts": "Todas las cuentas",
    "dateRange": "Fecha contable",
    "noEntries": "No hay extractos",
    "statementHeader": "Extracto",
    "columnDate": "Fecha / Valor",
    "columnTransaction": "Movimiento",
    "columnAmount": "Importe EUR",
    "assignmentHeader": "Asignación",
    "loadMore": "Cargar más",
    "stampBooked": "Contabilizado",
    "stampIgnored": "Ignorado",
    "stampReopened": "Reabierto"
  }
}
</i18n>
